import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { API, resolveImg } from '../api';

const AUTOPLAY_MS = 5000;

const BannerCarousel = () => {
  const [banners, setBanners] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [perView, setPerView] = useState(1);
  const timerRef = useRef(null);

  // Responsive per-view: 1 on mobile, 2 on md+
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(min-width: 768px)');
    const apply = () => setPerView(mq.matches ? 2 : 1);
    apply();
    mq.addEventListener ? mq.addEventListener('change', apply) : mq.addListener(apply);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', apply) : mq.removeListener(apply);
    };
  }, []);

  useEffect(() => {
    axios.get(`${API}/banners`).then((res) => {
      setBanners(Array.isArray(res.data) ? res.data : []);
    }).catch(() => setBanners([]));
  }, []);

  const n = banners.length;
  const step = 100 / perView; // width of a single banner as a % of the viewport
  // Autoplay advances one BANNER at a time and wraps around cleanly. We clone
  // the first `perView` banners at the end of the track so that when idx moves
  // past `n - 1` the viewer still sees a full row of images while we snap the
  // transform back to 0.
  const pageCount = n;
  const displayList = n > perView ? banners.concat(banners.slice(0, perView)) : banners;
  const disableTransition = idx >= n; // used briefly during the snap-back

  const goTo = useCallback((i) => {
    if (n === 0) return;
    setIdx(((i % n) + n) % n);
  }, [n]);

  const next = useCallback(() => setIdx((i) => (i + 1) % (n || 1)), [n]);
  const prev = useCallback(() => setIdx((i) => ((i - 1) + n) % (n || 1)), [n]);

  useEffect(() => {
    if (paused || pageCount < 2) return;
    timerRef.current = setTimeout(() => {
      setIdx((i) => i + 1); // may temporarily exceed n-1 to enter the clone zone
    }, AUTOPLAY_MS);
    return () => clearTimeout(timerRef.current);
  }, [idx, paused, pageCount]);

  // After sliding into the clone zone (idx === n), disable transition and snap
  // back to idx = 0 so the next real advance is smooth.
  useEffect(() => {
    if (idx >= n && n > 0) {
      const t = setTimeout(() => setIdx(0), 550); // slightly > CSS transition
      return () => clearTimeout(t);
    }
  }, [idx, n]);

  const dots = useMemo(() => Array.from({ length: pageCount }, (_, i) => i), [pageCount]);

  if (banners.length === 0) return null;

  const isExternal = (url) => /^https?:\/\//i.test(url || '');

  const SlideMedia = ({ b }) => (
    <div className="relative w-full h-full">
      <img
        src={resolveImg(b.image)}
        alt={b.title || 'offer banner'}
        className="w-full h-full object-cover"
        loading="lazy"
      />
      {b.ribbon_enabled && (
        <div className="absolute top-0 left-0 w-24 h-24 overflow-hidden pointer-events-none" data-testid="banner-ribbon">
          <div
            className="absolute bg-gradient-to-r from-rose-600 to-red-500 text-white text-[10px] md:text-[11px] font-bold uppercase tracking-wider text-center shadow-lg"
            style={{
              width: 140,
              padding: '4px 0',
              top: 22,
              left: -38,
              transform: 'rotate(-45deg)',
              letterSpacing: '0.08em',
            }}
          >
            {b.ribbon_text || 'Limited Time'}
          </div>
        </div>
      )}
      {(b.title || b.subtitle) && (
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/25 to-transparent flex items-end md:items-center">
          <div className="px-4 md:px-6 py-3 md:py-0 w-full">
            <div className="max-w-md text-white">
              {b.title && <h3 className="text-lg md:text-2xl font-extrabold leading-tight drop-shadow">{b.title}</h3>}
              {b.subtitle && <p className="text-[11px] md:text-sm mt-1 text-white/90 drop-shadow line-clamp-2">{b.subtitle}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <section
      className="max-w-[1200px] mx-auto px-4 md:px-6 mt-6 md:mt-10"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="banner-carousel"
    >
      <div className="relative overflow-hidden rounded-2xl">
        <div
          className={`flex h-[210px] sm:h-[260px] md:h-[220px] lg:h-[260px] ${disableTransition ? '' : 'transition-transform duration-500 ease-out'}`}
          style={{ transform: `translateX(-${idx * step}%)` }}
        >
          {displayList.map((b, i) => (
            <div
              key={`${b.id}-${i}`}
              className="h-full shrink-0 px-1.5 md:px-2"
              style={{ width: `${step}%` }}
            >
              <div className="w-full h-full rounded-xl overflow-hidden bg-gray-100 shadow-sm">
                {b.link_url ? (
                  isExternal(b.link_url) ? (
                    <a href={b.link_url} target="_blank" rel="noreferrer" className="block w-full h-full" data-testid={`banner-slide-${b.id}`}>
                      <SlideMedia b={b} />
                    </a>
                  ) : (
                    <Link to={b.link_url} className="block w-full h-full" data-testid={`banner-slide-${b.id}`}>
                      <SlideMedia b={b} />
                    </Link>
                  )
                ) : (
                  <div className="w-full h-full" data-testid={`banner-slide-${b.id}`}>
                    <SlideMedia b={b} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {pageCount > 1 && (
          <>
            <button
              onClick={prev}
              aria-label="Previous banner"
              data-testid="banner-prev-btn"
              className="absolute top-1/2 -translate-y-1/2 left-1 md:left-2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/85 hover:bg-white text-gray-800 shadow-md flex items-center justify-center backdrop-blur transition"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={next}
              aria-label="Next banner"
              data-testid="banner-next-btn"
              className="absolute top-1/2 -translate-y-1/2 right-1 md:right-2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/85 hover:bg-white text-gray-800 shadow-md flex items-center justify-center backdrop-blur transition"
            >
              <ChevronRight size={18} />
            </button>

            <div className="absolute bottom-2 md:bottom-3 left-0 right-0 flex items-center justify-center gap-1.5">
              {dots.map((i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  aria-label={`Go to banner ${i + 1}`}
                  data-testid={`banner-dot-${i}`}
                  className={`h-2 rounded-full transition-all ${i === (idx % n) ? 'w-6 bg-white' : 'w-2 bg-white/60 hover:bg-white/80'}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default BannerCarousel;
